// Copy/download helpers for Transcript History. The .docx is built in the browser with the `docx` package,
// loaded only when someone asks for it so it stays out of the main bundle.

export interface TranscriptMeta {
  title: string;
  description?: string | null;
  date: string;
  language?: string;
}

// Word copes with very long paragraphs, but a multi-hour transcript with no line breaks is one giant paragraph that
// makes editing painful. Split at whitespace into chunks of about this size so nothing is dropped or truncated.
const MAX_PARAGRAPH_CHARS = 3000;

export function splitIntoParagraphs(text: string): string[] {
  const paragraphs: string[] = [];
  for (const block of text.split(/\r?\n/)) {
    let rest = block.trim();
    if (rest === "") continue;
    while (rest.length > MAX_PARAGRAPH_CHARS) {
      // Prefer ending a chunk at the end of a sentence, otherwise at a space, otherwise hard-cut.
      const window = rest.slice(0, MAX_PARAGRAPH_CHARS);
      let cut = Math.max(window.lastIndexOf(". "), window.lastIndexOf("? "), window.lastIndexOf("! "));
      cut = cut > MAX_PARAGRAPH_CHARS / 2 ? cut + 1 : window.lastIndexOf(" ");
      if (cut <= 0) cut = MAX_PARAGRAPH_CHARS;
      paragraphs.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest) paragraphs.push(rest);
  }
  return paragraphs;
}

export function transcriptAsText(transcript: string, meta: TranscriptMeta): string {
  const header = [meta.title, meta.description?.trim(), `${meta.date}${meta.language ? ` (${meta.language})` : ""}`]
    .filter(Boolean)
    .join("\n");
  return `${header}\n\n${transcript}`;
}

export async function buildDocxBlob(transcript: string, meta: TranscriptMeta): Promise<Blob> {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel } = await import("docx");
  const children = [
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun(meta.title)] }),
  ];
  if (meta.description?.trim()) {
    children.push(new Paragraph({ children: [new TextRun({ text: meta.description.trim(), italics: true })] }));
  }
  children.push(
    new Paragraph({
      spacing: { after: 240 },
      children: [new TextRun({ text: `${meta.date}${meta.language ? ` (${meta.language})` : ""}`, color: "444444" })],
    })
  );
  for (const text of splitIntoParagraphs(transcript)) {
    children.push(new Paragraph({ spacing: { after: 160 }, children: [new TextRun(text)] }));
  }
  return Packer.toBlob(new Document({ sections: [{ children }] }));
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function safeFileName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim().slice(0, 80);
  return cleaned || "transcript";
}
